import {
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validator,
} from '@angular/forms';
import * as _ from 'lodash';

import { i18nService } from '../../services/i18n.service';
import { ConfigSliderModel } from './config-slider.model';
import { SliderAmountInputService } from './slider-amount-input.service';
import { SliderCurrencyPipe } from './slider-currency.pipe';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'slider-amount-input',
  template: `
    <div class="input-wrapper">
      @if (label != '') {
        <label class="input-label" [attr.for]="id"
          >{{ config.label }}
          @if (!optional) {
            <sup class="asterisk">*</sup>
          }
        </label>
      }

      @if (xDeleteButton && !isDisabled) {
        <span class="x-delete" (click)="deleteField()">x</span>
      }
      <input
        #sliderInput
        type="text"
        ondrop="return false;"
        onpaste="return false"
        appSliderParseDecimals
        decimalSeparator="{{ DECIMAL_SEPARATOR }}"
        [limits]="sliderLimits"
        [numFraction]="numFraction"
        (keypress)="_KeyPress($event)"
        (updateDecimals)="updateDecimals($event)"
        id="{{ id }}"
        class="form-control input-text"
        [value]="data"
        [disabled]="isDisabled"
        [class]="styleClass"
        [ngClass]="{ 'amount-error': isInvalid }"
      />
      <span class="number-span-plus">
        <i [ngClass]="iconPlus" aria-hidden="true" (click)="increaseValue()"></i>
      </span>
      <span class="number-span-minus">
        <i [ngClass]="iconMinus" aria-hidden="true" (click)="decreaseValue()"></i>
      </span>
      <span class="number-span-euro">{{ symbol }}</span>
      <div [formGroup]="sliderForm">
        <div class="slidecontainer limit">
          <p-slider
            [id]="'slider-' + id"
            formControlName="slider"
            [min]="min"
            [animate]="'true'"
            [max]="max"
            styleClass="slider"
            [step]="step"
            (onChange)="handleChange($event)"
            (onSlideEnd)="handleChange($event)"
          ></p-slider>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .error-message {
        color: red;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderAmountInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SliderAmountInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class SliderAmountInputComponent
  implements ControlValueAccessor, Validator, OnInit
{
  @ViewChild('sliderInput', { static: true }) sliderInput: any;
  @Input() config: ConfigSliderModel; // Component config object

  public sliderForm: UntypedFormGroup;

  public DECIMAL_SEPARATOR: string;
  private THOUSANDS_SEPARATOR: string;

  private format;
  private modelData: any;
  private lastValue: string = '';
  private newValue: string = '';

  public id: string = '';
  public label: string = '';
  public optional: boolean;
  public isDisabled: boolean = false;
  public styleClass: string = '';
  public inputFormat: string = '';
  public symbol: string = '';
  public xDeleteButton: boolean = true;
  public iconPlus: string = '';
  public iconMinus: string = '';

  public min: number;
  public max: number;
  public sliderLimits: any;
  public step: number;
  public decrease: number;
  public increase: number;
  public errorMessages: Object[] = [];
  public errors: Object = {};
  public isInvalid: boolean = false;
  public data: any;

  public numFraction: number;

  constructor(
    private el: ElementRef,
    private formBuilder: UntypedFormBuilder,
    public _i18n: i18nService,
    private sliderAmountInputService: SliderAmountInputService,
    private sliderCurrencyPipe: SliderCurrencyPipe,
  ) {}

  private getFormat(inputFormat: string) {
    let regex: string = '^$|^[0-9]*(' + this.DECIMAL_SEPARATOR + '[0-9]{0,2})?$';
    const [integer, fraction = ''] = (inputFormat || '').toString().split('-');

    if (integer === '' || integer === '*') {
      regex = '^$|^[0-9]*';
    } else {
      regex = '^$|^[0-9]{0,' + integer + '}';
    }

    if (fraction === '' || fraction === '*') {
      regex += '(' + this.DECIMAL_SEPARATOR + '[0-9]*)?$';
    } else {
      regex += '(' + this.DECIMAL_SEPARATOR + '[0-9]{0,' + fraction + '})?$';
      this.sliderAmountInputService.setFractionSize(parseInt(fraction, 10));
    }
    this.numFraction = parseInt(fraction, 10);
    return regex;
  }

  private getFormatRegExp(inputFormat: string) {
    return new RegExp(inputFormat);
  }

  private formatInput(): void {
    if (this.data) {
      this.getFormat(this.config.inputFormat);
      this.data = this.sliderCurrencyPipe.transform(this.data);

      if ((<string>this.data).startsWith(this.DECIMAL_SEPARATOR)) {
        this.data = `0${this.data}`;
      }
      this.data = this.sliderCurrencyPipe.transform(this.data);
    }
  }

  private checkLimits(amount: any) {
    let value: any = amount;

    if (amount < this.min) {
      value = this.min;
    }

    if (amount > this.max) {
      value = this.max;
    }

    return value;
  }

  private convertNumberFormat(amount: any) {
    let value: any;

    if (typeof amount !== 'number') {
      value = this.sliderCurrencyPipe.parse(amount);
    } else {
      value = amount;
    }

    return value;
  }

  public increaseValue() {
    const increase: number = this.increase;
    let value: any;

    this.lastValue = this.convertNumberFormat(this.lastValue);
    if (this.lastValue === '') {
      this.lastValue = '0';
    }
    this.data = parseFloat(this.lastValue) + increase;

    this.data = this.checkLimits(this.data);

    this.lastValue = this.data;
    this.formatInput();
    value = this.convertNumberFormat(this.data);

    this.modelData = parseFloat(value);

    this.propagateChange(this.modelData);
    this.sliderForm.controls.slider.setValue(this.modelData);
  }

  public decreaseValue() {
    const decrease: number = this.decrease;
    let value: any;
    this.lastValue = this.convertNumberFormat(this.lastValue);
    if (this.lastValue === '') {
      this.lastValue = '0';
    }
    this.data = parseFloat(this.lastValue) - decrease;

    this.data = this.checkLimits(this.data);

    this.lastValue = this.data;
    this.formatInput();
    value = this.convertNumberFormat(this.data);

    this.modelData = parseFloat(value);

    this.propagateChange(this.modelData);
    this.sliderForm.controls.slider.setValue(this.modelData);
  }

  public handleChange(event: any) {
    // calcular las barras
    let barValue: any;

    if (isNaN(event.value)) {
      // Evitar que se quede pillado el slider
      this.sliderForm.controls.slider.setValue(0);
      return;
    }

    event.value = this.checkLimits(event.value);

    this.data = event.value;
    this.lastValue = event.value;
    this.formatInput();

    barValue = this.convertNumberFormat(this.data);

    this.modelData = parseFloat(barValue);

    this.propagateChange(this.modelData);
  }

  public updateDecimals(event) {
    let currentValue = event;

    if (currentValue !== '') {
      currentValue = this.checkLimits(currentValue);
      this.sliderForm.controls['slider'].setValue(parseFloat(currentValue));

      if (!this.format.test(currentValue)) {
        this.sliderInput.nativeElement.value = this.lastValue;

        this.newValue = this.convertNumberFormat(this.lastValue);
        this.data = this.newValue;

        // update the form
        this.modelData = parseFloat(
          this.data.toString().replace(this.DECIMAL_SEPARATOR, '.'),
        );
        this.propagateChange(this.modelData);
      } else {
        this.lastValue = currentValue;
        this.data = currentValue;
        this.modelData = parseFloat(
          this.data.toString().replace(this.DECIMAL_SEPARATOR, '.'),
        );
        this.propagateChange(this.modelData);
      }
    }
  }

  public deleteField() {
    this.getFormat(this.config.inputFormat);
    this.data = this.sliderCurrencyPipe.transform('0');
    this.sliderForm.controls['slider'].setValue(0);
    this.propagateChange(this.data);
  }

  private setdata() {
    this.getFormat(this.config.inputFormat);
    this.data = this.sliderCurrencyPipe.transform(this.data);
    this.propagateChange(this.data);
  }

  // this is the initial value set to the component
  public writeValue(obj: any) {
    this.data = obj;
    this.sliderForm.controls.slider.setValue(this.data);
  }

  // registers 'fn' that will be fired wheb changes are made
  // this is how we emit the changes back to the form
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
    this.setdata();
  }

  // validates the form, returns null when valid else the validation object
  // in this case we're checking if the json parsing has passed or failed from the onChange method
  public validate(c: UntypedFormControl) {
    setTimeout(() => {
      this.lastValue = c.value;
      this.isInvalid = c.invalid && c.dirty;
      this.errors = c.errors;
    }, 0);

    return null;
  }

  // not used, used for touch input
  public registerOnTouched() {}

  _KeyPress(event: any) {
    if (!this.format.test(event.target.value)) {
      // invalid character, prevent input
      event.preventDefault();
    }
  }

  // change events from the text input
  public onChange(event: any) {}

  // the method set in registerOnChange to emit changes back to the form
  private propagateChange = (_: any) => {};

  private initForm() {
    const slider = 0;

    this.sliderForm = this.formBuilder.group({
      slider: [slider, []],
    });
  }

  resetcomponet(inputAmount: string) {
    this.data = inputAmount;
    this.setdata();

    this.sliderAmountInputService.getLocaleSeparators();
    this.DECIMAL_SEPARATOR = this.sliderAmountInputService.getDecimalSeparator();
    this.THOUSANDS_SEPARATOR = this.sliderAmountInputService.getThousandsSeparator();
    this.setConfingParams();

    // Meter una demora que rompe la asincronia para y actualice correctamente. (SE ACEPTAN POSIBLES MEJORES SOLUCIONES).
    setTimeout(() => {
      this.sliderForm.controls.slider.setValue(
        parseFloat(this.sliderCurrencyPipe.parse(inputAmount)),
      );
    }, 0);
  }

  setConfingParams() {
    const config: any = this.config;

    if (_.isUndefined(config.id) || config.id === '') {
      throw new Error('VASS core: id is required');
    } else {
      this.id = _.isUndefined(config.id) ? '' : config.id;
      this.label = _.isUndefined(config.label) ? '' : config.label;
      this.optional = _.isUndefined(config.optional) ? false : config.optional;
      this.inputFormat = _.isUndefined(config.inputFormat)
        ? '^$|^[0-9]*(' + this.DECIMAL_SEPARATOR + '[0-9]{0,2})?$'
        : this.getFormat(config.inputFormat);
      this.isDisabled = _.isUndefined(config.isDisabled) ? false : config.isDisabled;
      this.styleClass = _.isUndefined(config.styleClass) ? '' : config.styleClass;
      this.xDeleteButton = _.isUndefined(config.xDeleteButton)
        ? true
        : config.xDeleteButton;
      this.min = _.isUndefined(config.min) ? 0 : config.min;
      this.max = _.isUndefined(config.max) ? 1000 : config.max;
      this.step = _.isUndefined(config.step) ? 10 : config.step;
      this.decrease = _.isUndefined(config.decrease) ? 1000 : config.decrease;
      this.increase = _.isUndefined(config.increase) ? 1000 : config.increase;
      this.symbol = _.isUndefined(config.symbol) ? '€' : config.symbol;
      this.iconPlus = _.isUndefined(config.iconPlus) ? 'fa fa-plus' : config.iconPlus;
      this.iconMinus = _.isUndefined(config.iconMinus) ? 'fa fa-minus' : config.iconMinus;

      this.errorMessages = _.isUndefined(config.errorMessages)
        ? []
        : config.errorMessages;
      this.format = this.getFormatRegExp(this.inputFormat);
      this.sliderLimits = { min: this.min, max: this.max };
    }
  }
  ngOnInit() {
    this.initForm();

    this.sliderAmountInputService.getLocaleSeparators();
    this.DECIMAL_SEPARATOR = this.sliderAmountInputService.getDecimalSeparator();
    this.THOUSANDS_SEPARATOR = this.sliderAmountInputService.getThousandsSeparator();

    this.setConfingParams();
  }
}
